import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  ViewChild
} from '@angular/core';
import {
  ControlContainer,
  NgForm
} from '@angular/forms';

import '../../../../vendor/bootstrap-datepicker/bootstrap-datepicker.min';

import { LEGACY_DATE }            from './legacy-date.constant';
import { LegacyDateKindModel }    from './typings/legacy-date.kind';
import { LegacyDateOptionsModel } from './typings/legacy-date.options';
import { LegacyDateService }      from './legacy-date.service';
import { TextControlComponent }   from '../text-control/text-control.component';

@Component({
  selector   : '[worLegacyDate]',
  template: require('./legacy-date-control.component.html'),

  viewProviders: [
    {
      provide    : ControlContainer,
      useExisting: NgForm
    }
  ]
})
export class LegacyDateControlComponent extends TextControlComponent implements AfterViewInit, OnDestroy {
  private _picker    : JQuery = null;
  private _viewValue = '';

  _filterMaxlength = LEGACY_DATE.filterMaxlength;
  _kind            : LegacyDateKindModel = 'date';
  _shownText       : string = null;
  _startDate       = '';
  _text            = '';

  @Input() addonClass    = '';
  @Input() hasPicker     = true;
  @Input() inputClass    = '';
  @Input() inputTabindex : number | string = null;
  @Input() isFilter      = false;
  @Input() isReadonly    = false;
  @Input() modelFormat   = 'date';

  // The model can arrive before the kind, and only the kind knows how to show it.
  @Input() set kind ( value : LegacyDateKindModel ) {
    this._kind = value || 'date';

    this._showModel();
  }

  @Input() set model ( value : string ) {
    const isNew = value !== this._model;

    this._model = value;

    if ( isNew ) {
      this._showModel();
    }
  }

  @Input() set startDate ( value : string ) {
    this._startDate = value || '';

    if ( this._picker ) {
      this.ngZone.runOutsideAngular(() => this._picker.datepicker('setStartDate', this.legacyDateService.pickerStartDate(this._pickerStart())));
    }

    // A date refused under the old start date is taken once the new one allows it.
    if ( this._text && !this._model ) {
      Promise.resolve().then(() => this._read());
    }
  }

  @ViewChild('addon') addon : ElementRef<HTMLElement>;

  constructor (
    private legacyDateService: LegacyDateService,
    private ngZone           : NgZone
  ) {
    super();
  }

  private _handlePick ( date : Date ) : void {
    if ( this._disabled ) {
      return;
    }

    const text = this.legacyDateService.formatPick(date, this._kind);

    if ( ( this.elementRef.nativeElement as HTMLInputElement ).value !== text ) {
      this._setInputText(text);
    }
  }

  private _options () : LegacyDateOptionsModel {
    return {
      isFilter   : this.isFilter,
      kind       : this._kind,
      modelFormat: this.modelFormat,
      startDate  : this._startDate
    };
  }

  // The AngularJS report, job and month fields have no start date.
  private _pickerStart () : string {
    return this.legacyDateService.isDate(this._kind)
      ? this._startDate
      : '';
  }

  // Shows the text as AngularJS redrew it, such as a date without its surrounding spaces.
  private _read () : void {
    const { text, value } = this.legacyDateService.parse(this._text, this._options());

    // AngularJS writes no value while a required field is empty.
    const model = this._required && this.legacyDateService.isBlank(this._text)
      ? undefined
      : value;

    if ( text !== undefined && text !== this._text ) {
      this._text = text;
    }

    if ( model !== this._model ) {
      this._model = model;

      this.modelChange.emit(model);
    }
  }

  // Goes through the input event, so the form sees it as typed.
  private _setInputText ( text : string ) : void {
    const input = this.elementRef.nativeElement as HTMLInputElement;

    input.value = text;

    input.dispatchEvent(new Event('input'));
  }

  private _showModel () : void {
    this._text      = this.legacyDateService.format(this._model, this._kind);
    this._shownText = this._text;

    // AngularJS keeps no view value for a stored month it cannot read, so clearing the field still sends ''.
    this._viewValue = this.legacyDateService.isMonth(this._kind) && this._model && !this._text
      ? null
      : this._text;
  }

  ngAfterViewInit () : void {
    super.ngAfterViewInit();

    if ( !this.addon ) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      this._picker = $(this.addon.nativeElement)
      .datepicker(this.legacyDateService.pickerOptions(this._pickerStart(), this._kind))
      .on('changeDate', ( event : JQuery.TriggeredEvent & { date : Date } ) => this._handlePick(event.date));
    });
  }

  ngOnDestroy () : void {
    if ( this._picker ) {
      this._picker.off('changeDate').datepicker('remove');
    }
  }

  // A link the page puts beside a filter field clears it.
  @HostListener('click', [ '$event' ])
  onClick ( event : MouseEvent ) : void {
    if ( this.isFilter && ( event.target as HTMLElement ).closest('a') ) {
      this._setInputText('');
    }
  }

  onEscape ( event : Event ) : void {
    if ( this.isFilter ) {
      this._setInputText('');

      event.stopPropagation();
    }
  }

  // AngularJS reads the field again only when its trimmed text changes.
  onTextChange () : void {
    const trimmed = ( this._text || '' ).trim();

    if ( trimmed !== this._viewValue ) {
      this._shownText = null;
      this._viewValue = trimmed;

      this._read();
    }
  }
}
